import React, { useCallback, useMemo } from 'react';
import { Icon } from '../';
import type { FolderTreeProps, TreeFolder, IndentSize } from './FolderTree.types';

import styles from './FolderTree.module.css';

/**
 * Get the CSS class for indentation based on size
 */
const getIndentClass = (size: IndentSize): string => {
  switch (size) {
    case 'sm':
      return styles.indentSm ?? '';
    case 'lg':
      return styles.indentLg ?? '';
    default:
      return styles.indentMd ?? '';
  }
};

/**
 * TreeItem component for rendering individual folder items
 */
interface TreeItemInternalProps {
  folder: TreeFolder;
  depth: number;
  isSelected: boolean;
  isExpanded: boolean;
  indentSize: IndentSize;
  showCounts: boolean;
  expandedIds: string[];
  selectedId: string | null | undefined;
  onSelect: (folder: TreeFolder) => void;
  onToggle: (folderId: string, expand: boolean) => void;
  onLoadChildren?: (folderId: string) => Promise<void>;
}

const TreeItem: React.FC<TreeItemInternalProps> = ({
  folder,
  depth,
  isSelected,
  isExpanded,
  indentSize,
  showCounts,
  expandedIds,
  selectedId,
  onSelect,
  onToggle,
  onLoadChildren,
}) => {
  const hasChildren = (folder.children && folder.children.length > 0) || 
                      (folder.foldersCount !== undefined && folder.foldersCount > 0);
  const isLoading = folder.isLoading || false;

  const handleClick = useCallback(() => {
    onSelect(folder);
  }, [folder, onSelect]);

  const handleToggle = useCallback(async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    
    if (isLoading) return;
    
    const newExpandedState = !isExpanded;
    
    if (newExpandedState && onLoadChildren && !folder.children?.length) {
      await onLoadChildren(folder.id);
    }
    
    onToggle(folder.id, newExpandedState);
  }, [folder.id, folder.children?.length, isExpanded, isLoading, onToggle, onLoadChildren]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleClick();
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (!isExpanded && hasChildren) {
          handleToggle(e);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (isExpanded && hasChildren) {
          handleToggle(e);
        }
        break;
      default:
        break;
    }
  }, [handleClick, handleToggle, isExpanded, hasChildren]);

  const handleToggleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(e);
    }
  }, [handleToggle]);

  const indentClass = getIndentClass(indentSize);

  return (
    <li
      className={styles.treeItem}
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={isSelected}
    >
      <div
        className={`${styles.treeItemRow} ${indentClass} ${isSelected ? styles.selected : ''}`}
        style={{ '--depth': depth } as React.CSSProperties}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-label={`Folder: ${folder.name}`}
        data-folder-id={folder.id}
      >
        {hasChildren ? (
          isLoading ? (
            <div className={styles.loadingIndicator} aria-label="Loading">
              <div className={styles.spinner} />
            </div>
          ) : (
            <button
              className={styles.toggleButton}
              onClick={handleToggle}
              onKeyDown={handleToggleKeyDown}
              aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
              tabIndex={-1}
            >
              <Icon
                name="chevron_right"
                size="sm"
                className={`${styles.toggleIcon} ${isExpanded ? styles.expanded : ''}`}
              />
            </button>
          )
        ) : (
          <div className={styles.togglePlaceholder} aria-hidden="true" />
        )}

        <div className={`${styles.folderIcon} ${isSelected ? styles.selected : ''}`}>
          <Icon name={isExpanded ? 'folder_open' : 'folder'} size="sm" />
        </div>

        <span className={`${styles.folderName} ${isSelected ? styles.selected : ''}`}>
          {folder.name}
        </span>

        {showCounts && (
          <div className={styles.counts}>
            {folder.foldersCount !== undefined && folder.foldersCount > 0 && (
              <span className={styles.countBadge}>
                <Icon name="folder" size="sm" className={styles.countIcon} />
                {folder.foldersCount}
              </span>
            )}
            {folder.resourcesCount !== undefined && folder.resourcesCount > 0 && (
              <span className={styles.countBadge}>
                <Icon name="insert_drive_file" size="sm" className={styles.countIcon} />
                {folder.resourcesCount}
              </span>
            )}
          </div>
        )}
      </div>

      {hasChildren && folder.children && folder.children.length > 0 && (
        <ul
          className={`${styles.treeListNested} ${styles.childrenContainer} ${!isExpanded ? styles.collapsed : ''}`}
          role="group"
        >
          {folder.children.map((child) => (
            <TreeItem
              key={child.id}
              folder={child}
              depth={depth + 1}
              isSelected={selectedId === child.id}
              isExpanded={expandedIds.includes(child.id)}
              indentSize={indentSize}
              showCounts={showCounts}
              expandedIds={expandedIds}
              selectedId={selectedId}
              onSelect={onSelect}
              onToggle={onToggle}
              onLoadChildren={onLoadChildren}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/**
 * FolderTree component for hierarchical folder navigation
 * Based on IntelligenceBank folder navigation patterns
 */
export const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  selectedId = null,
  expandedIds = [],
  onSelect,
  onToggleExpand,
  onLoadChildren,
  showCounts = false,
  showRoot = false,
  rootName = 'Home',
  indentSize = 'md',
  loading = false,
  emptyMessage = 'No folders found',
  className = '',
  'data-testid': testId,
  dataTestId,
  dataId,
}) => {
  const handleSelect = useCallback((folder: TreeFolder) => {
    onSelect?.(folder);
  }, [onSelect]);

  const handleToggle = useCallback((folderId: string, expand: boolean) => {
    onToggleExpand?.(folderId, expand);
  }, [onToggleExpand]);

  const rootFolder: TreeFolder = useMemo(() => ({
    id: 'root',
    name: rootName,
    parentId: null,
    children: folders,
    foldersCount: folders.length,
  }), [rootName, folders]);

  const isRootSelected = selectedId === 'root';
  const isRootExpanded = expandedIds.includes('root') || !showRoot;

  // Resolve test ID with new props taking precedence
  const resolvedTestId = dataTestId || testId;

  if (loading) {
    return (
      <div
        className={`${styles.tree} ${className}`}
        data-testid={resolvedTestId}
        data-id={dataId}
        role="tree"
        aria-label="Folder tree loading"
      >
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} aria-label="Loading folders" />
        </div>
      </div>
    );
  }

  if (folders.length === 0 && !showRoot) {
    return (
      <div
        className={`${styles.tree} ${className}`}
        data-testid={resolvedTestId}
        data-id={dataId}
        role="tree"
        aria-label="Empty folder tree"
      >
        <div className={styles.emptyState}>
          <Icon name="folder_off" size="xl" className={styles.emptyIcon} />
          <p className={styles.emptyMessage}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.tree} ${className}`}
      data-testid={resolvedTestId}
      data-id={dataId}
    >
      <ul className={styles.treeList} role="tree" aria-label="Folder navigation">
        {showRoot ? (
          <li
            className={`${styles.treeItem} ${styles.rootItem}`}
            role="treeitem"
            aria-expanded={isRootExpanded}
            aria-selected={isRootSelected}
          >
            <div
              className={`${styles.treeItemRow} ${isRootSelected ? styles.selected : ''}`}
              onClick={() => handleSelect(rootFolder)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(rootFolder);
                }
              }}
              tabIndex={0}
              aria-label={`Root folder: ${rootName}`}
              data-folder-id="root"
            >
              <button
                className={styles.toggleButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle('root', !isRootExpanded);
                }}
                aria-label={isRootExpanded ? 'Collapse root folder' : 'Expand root folder'}
                tabIndex={-1}
              >
                <Icon
                  name="chevron_right"
                  size="sm"
                  className={`${styles.toggleIcon} ${isRootExpanded ? styles.expanded : ''}`}
                />
              </button>
              <div className={`${styles.folderIcon} ${isRootSelected ? styles.selected : ''}`}>
                <Icon name={isRootExpanded ? 'folder_open' : 'folder'} size="sm" />
              </div>
              <span className={`${styles.folderName} ${isRootSelected ? styles.selected : ''}`}>
                {rootName}
              </span>
              {showCounts && folders.length > 0 && (
                <div className={styles.counts}>
                  <span className={styles.countBadge}>
                    <Icon name="folder" size="sm" className={styles.countIcon} />
                    {folders.length}
                  </span>
                </div>
              )}
            </div>
            {isRootExpanded && folders.length > 0 && (
              <ul className={styles.treeListNested} role="group">
                {folders.map((folder) => (
                  <TreeItem
                    key={folder.id}
                    folder={folder}
                    depth={1}
                    isSelected={selectedId === folder.id}
                    isExpanded={expandedIds.includes(folder.id)}
                    indentSize={indentSize}
                    showCounts={showCounts}
                    expandedIds={expandedIds}
                    selectedId={selectedId}
                    onSelect={handleSelect}
                    onToggle={handleToggle}
                    onLoadChildren={onLoadChildren}
                  />
                ))}
              </ul>
            )}
          </li>
        ) : (
          folders.map((folder) => (
            <TreeItem
              key={folder.id}
              folder={folder}
              depth={0}
              isSelected={selectedId === folder.id}
              isExpanded={expandedIds.includes(folder.id)}
              indentSize={indentSize}
              showCounts={showCounts}
              expandedIds={expandedIds}
              selectedId={selectedId}
              onSelect={handleSelect}
              onToggle={handleToggle}
              onLoadChildren={onLoadChildren}
            />
          ))
        )}
      </ul>
    </div>
  );
};

FolderTree.displayName = 'FolderTree';